import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, LayoutGrid } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '@reflex/ui/lib/utils';
import { MaturityBadge } from '@reflex/ui/components/shared/MaturityBadge';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Popover, PopoverTrigger, PopoverContent } from '@reflex/ui/components/ui/popover';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { orgBasePath, ROUTES } from '@/lib/routes';
import type { NavItem, NavDrillGroup } from './nav-types';
import {
  getActiveDrillGroup,
  getActiveDrillItemPath,
  groupContainsPath,
  navRowClasses,
  slideTransition,
} from './nav-utils';
import { NavBottomSection } from './NavDrill';

/**
 * The main-group links (Inbox, Sessions, plugin contributions) shared by the
 * drill top level and every alternative nav style. Owns the active-route
 * matching, including the Sessions special case where agent detail routes
 * highlight the Sessions entry.
 */
export function NavMainLinks({ items }: { items: NavItem[] }) {
  const { pathname } = useLocation();
  const slug = useActiveOrgSlug();
  const homePath = slug ? ROUTES.org(slug).home : '/';
  const runsPath = slug ? ROUTES.org(slug).runs : '/runs';
  const agentsPrefix = slug ? `${orgBasePath(slug)}/agents/` : '/agents/';
  return (
    <>
      {items.map(({ to, label, icon: Icon, trailing: Trailing, tutorialId, maturityStage }) => {
        const isHome = to === homePath;
        const active =
          pathname === to ||
          (!isHome && pathname.startsWith(to + '/')) ||
          (to === runsPath && pathname.startsWith(agentsPrefix));
        return (
          <NavLink
            key={to}
            to={to}
            end={isHome}
            data-testid={`nav-item-${to}`}
            data-tutorial-id={tutorialId}
            data-active={active ? 'true' : undefined}
            className={navRowClasses(active)}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {maturityStage && <MaturityBadge stage={maturityStage} size="compact" />}
            {Trailing && <Trailing />}
          </NavLink>
        );
      })}
    </>
  );
}

/** Props shared by all alternative nav style views. */
export interface NavStyleViewProps {
  allMainNavItems: NavItem[];
  mainDrillGroups: NavDrillGroup[];
  bottomDrillGroups: NavDrillGroup[];
  pluginBottomItems: NavItem[];
  /**
   * Every visible drill group including sub-drills (`parentBasePath` set).
   * Used to resolve nested groups (Organization > Integrations) and the
   * active group for bottom-section highlighting.
   */
  drillGroups: NavDrillGroup[];
}

function useSubGroupsByPath(drillGroups: NavDrillGroup[]): ReadonlyMap<string, NavDrillGroup> {
  return useMemo(
    () => new Map(drillGroups.filter((g) => g.parentBasePath).map((g) => [g.basePath, g])),
    [drillGroups],
  );
}

/* -------------------------------- Expand -------------------------------- */

interface ExpandGroupProps {
  group: NavDrillGroup;
  subGroupsByPath: ReadonlyMap<string, NavDrillGroup>;
}

/**
 * A collapsible tree section. The group containing the active route opens
 * automatically when the user navigates into it, but a manual close is
 * respected until the active group changes again.
 */
function ExpandGroup({ group, subGroupsByPath }: ExpandGroupProps) {
  const { pathname } = useLocation();
  const containsActive = groupContainsPath(group, pathname);
  const [open, setOpen] = useState(containsActive);

  // Derive-from-props via previous-render state: when the route moves into
  // this group, force it open. While the route stays inside, the user may
  // still collapse it manually.
  const [prevContainsActive, setPrevContainsActive] = useState(containsActive);
  if (prevContainsActive !== containsActive) {
    setPrevContainsActive(containsActive);
    if (containsActive && !open) setOpen(true);
  }

  const GroupIcon = group.icon;
  const overviewActive = pathname === group.basePath;
  const activeItemPath = getActiveDrillItemPath(group.items, pathname);
  const panelId = `nav-expand-panel-${group.basePath}`;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={`nav-expand-${group.basePath}`}
        data-tutorial-id={group.tutorialId}
        data-active={containsActive ? 'true' : undefined}
        className={navRowClasses(!open && containsActive, 'w-full')}
      >
        <GroupIcon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150',
            open && 'rotate-90',
          )}
        />
      </button>
      {/* Unmount collapsed content instead of hiding it at height 0: keeps
          keyboard focus and the accessibility tree honest (no tabbable links
          or nested toggle buttons inside an invisible panel). */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={slideTransition}
            className="overflow-hidden"
          >
            <div className="ml-[1.05rem] flex flex-col gap-0.5 border-l border-border/40 py-0.5 pl-1.5">
              <NavLink
                to={group.basePath}
                end
                data-testid={`nav-expand-overview-${group.basePath}`}
                data-active={overviewActive ? 'true' : undefined}
                className={navRowClasses(overviewActive)}
              >
                <LayoutGrid className="h-4 w-4 shrink-0" />
                Overview
              </NavLink>
              {group.items.map((item) => {
                const subGroup = subGroupsByPath.get(item.to);
                if (subGroup) {
                  return (
                    <ExpandGroup key={item.to} group={subGroup} subGroupsByPath={subGroupsByPath} />
                  );
                }
                const { to, label, icon: Icon, tutorialId } = item;
                const active = to === activeItemPath;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-item-${to}`}
                    data-tutorial-id={tutorialId}
                    data-active={active ? 'true' : undefined}
                    className={navRowClasses(active)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                  </NavLink>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function NavExpandView({
  allMainNavItems,
  mainDrillGroups,
  bottomDrillGroups,
  pluginBottomItems,
  drillGroups,
}: NavStyleViewProps) {
  const { pathname } = useLocation();
  const subGroupsByPath = useSubGroupsByPath(drillGroups);
  const activeGroup = getActiveDrillGroup(pathname, drillGroups);
  return (
    <div className="flex flex-1 min-h-0 flex-col scrollbar-fade" data-testid="nav-view-expand">
      <div className="flex flex-col gap-0.5 px-1.5">
        <NavMainLinks items={allMainNavItems} />
        {mainDrillGroups.map((group) => (
          <ExpandGroup key={group.basePath} group={group} subGroupsByPath={subGroupsByPath} />
        ))}
      </div>
      {/* Inside the scroll container with mt-auto: pinned to the bottom while
          content fits, scrolls naturally once an expanded tree outgrows the
          sidebar instead of clipping a fixed footer. */}
      <div className="mt-auto">
        <NavBottomSection
          pluginBottomItems={pluginBottomItems}
          bottomDrillGroups={bottomDrillGroups}
          activeDrillGroup={activeGroup}
          renderGroup={(group) => <ExpandGroup group={group} subGroupsByPath={subGroupsByPath} />}
        />
      </div>
    </div>
  );
}

/* --------------------------------- Flat --------------------------------- */

/**
 * An always-expanded section: a linked section label (the group's overview
 * page) followed by its items. The whole sidebar becomes one scrolling list.
 */
function FlatSection({ group }: { group: NavDrillGroup }) {
  const { pathname } = useLocation();
  const activeItemPath = getActiveDrillItemPath(group.items, pathname);
  return (
    <div className="flex flex-col gap-0.5" data-testid={`nav-flat-${group.basePath}`}>
      <NavLink
        to={group.basePath}
        end
        data-testid={`nav-flat-overview-${group.basePath}`}
        data-tutorial-id={group.tutorialId}
        className="group/flat mt-2 flex items-center rounded-md px-2.5 pb-0.5 pt-1.5"
      >
        {({ isActive }) => (
          <SectionLabel
            as="span"
            className={cn(
              'min-w-0 truncate transition-colors group-hover/flat:text-sidebar-foreground',
              isActive && 'text-sidebar-accent-foreground',
            )}
          >
            {group.label}
          </SectionLabel>
        )}
      </NavLink>
      {group.items.map(({ to, label, icon: Icon, tutorialId }) => {
        const active = to === activeItemPath;
        return (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-item-${to}`}
            data-tutorial-id={tutorialId}
            data-active={active ? 'true' : undefined}
            className={navRowClasses(active)}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{label}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

export function NavFlatView({
  allMainNavItems,
  mainDrillGroups,
  bottomDrillGroups,
  pluginBottomItems,
  drillGroups,
}: NavStyleViewProps) {
  const { pathname } = useLocation();
  const activeGroup = getActiveDrillGroup(pathname, drillGroups);
  return (
    <div className="flex flex-1 min-h-0 flex-col scrollbar-fade" data-testid="nav-view-flat">
      <div className="flex flex-col gap-0.5 px-1.5">
        <NavMainLinks items={allMainNavItems} />
        {mainDrillGroups.map((group) => (
          <FlatSection key={group.basePath} group={group} />
        ))}
      </div>
      <div className="mt-auto">
        <NavBottomSection
          pluginBottomItems={pluginBottomItems}
          bottomDrillGroups={bottomDrillGroups}
          activeDrillGroup={activeGroup}
          renderGroup={(group) => <FlatSection group={group} />}
        />
      </div>
    </div>
  );
}

/* -------------------------------- Flyout -------------------------------- */

function flyoutItemClasses(active: boolean): string {
  return cn(
    'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
    active
      ? 'bg-selected text-selected-foreground font-medium'
      : 'text-foreground/80 hover:bg-accent/50',
  );
}

/**
 * A section that opens a floating panel beside the sidebar, mirroring the
 * collapsed rail's popovers but with a full-width labelled trigger row. The
 * panel closes on item click so navigation feels like a menu selection.
 */
function FlyoutGroup({ group, isActive }: { group: NavDrillGroup; isActive: boolean }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const activeItemPath = getActiveDrillItemPath(group.items, pathname);
  const overviewActive = pathname === group.basePath;
  const GroupIcon = group.icon;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`nav-flyout-${group.basePath}`}
          data-tutorial-id={group.tutorialId}
          data-active={isActive ? 'true' : undefined}
          className={navRowClasses(isActive, 'w-full')}
        >
          <GroupIcon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={10}
        className="w-52 p-1"
        data-testid={`nav-flyout-panel-${group.basePath}`}
      >
        <div className="flex flex-col gap-0.5">
          <SectionLabel as="span" className="block px-2.5 pb-1 pt-1.5">
            {group.label}
          </SectionLabel>
          <NavLink
            to={group.basePath}
            end
            onClick={() => setOpen(false)}
            data-testid={`nav-flyout-overview-${group.basePath}`}
            className={flyoutItemClasses(overviewActive)}
          >
            <LayoutGrid className="h-3.5 w-3.5 shrink-0" />
            Overview
          </NavLink>
          {group.items.map(({ to, label, icon: Icon, tutorialId }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              data-testid={`nav-item-${to}`}
              data-tutorial-id={tutorialId}
              className={flyoutItemClasses(to === activeItemPath)}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function NavFlyoutView({
  allMainNavItems,
  mainDrillGroups,
  bottomDrillGroups,
  pluginBottomItems,
  drillGroups,
}: NavStyleViewProps) {
  const { pathname } = useLocation();
  const activeGroup = getActiveDrillGroup(pathname, drillGroups);
  return (
    <div className="flex flex-1 min-h-0 flex-col" data-testid="nav-view-flyout">
      <div className="flex flex-1 min-h-0 flex-col gap-0.5 px-1.5 scrollbar-fade">
        <NavMainLinks items={allMainNavItems} />
        {mainDrillGroups.map((group) => (
          <FlyoutGroup key={group.basePath} group={group} isActive={activeGroup === group} />
        ))}
      </div>
      <NavBottomSection
        pluginBottomItems={pluginBottomItems}
        bottomDrillGroups={bottomDrillGroups}
        activeDrillGroup={activeGroup}
        renderGroup={(group, isActive) => <FlyoutGroup group={group} isActive={isActive} />}
      />
    </div>
  );
}
